import { useTranslation } from "react-i18next";
import type { ContactInfo } from "@/lib/types";

interface ContactStepProps {
  value: ContactInfo;
  onChange: (next: ContactInfo) => void;
  errors?: Partial<Record<keyof ContactInfo, string>>;
}

export function ContactStep({ value, onChange, errors }: ContactStepProps) {
  const { t } = useTranslation();
  const update = <K extends keyof ContactInfo>(key: K, v: ContactInfo[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="card md:max-w-wizard">
      <h2 className="font-display text-h3 text-ink">{t("q.contact._label")}</h2>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Field label={t("q.contact.prenom")} error={errors?.prenom}>
          <input
            type="text"
            value={value.prenom}
            onChange={(e) => update("prenom", e.target.value)}
            className={"input " + (errors?.prenom ? "input-error" : "")}
            autoComplete="given-name"
          />
        </Field>

        <Field label={t("q.contact.email")} error={errors?.email}>
          <input
            type="email"
            value={value.email}
            onChange={(e) => update("email", e.target.value)}
            className={"input " + (errors?.email ? "input-error" : "")}
            autoComplete="email"
            inputMode="email"
          />
        </Field>

        <Field label={t("q.contact.whatsapp")} error={errors?.whatsapp}>
          <input
            type="tel"
            value={value.whatsapp}
            onChange={(e) => update("whatsapp", e.target.value)}
            placeholder="+33 6 XX XX XX XX"
            className={"input " + (errors?.whatsapp ? "input-error" : "")}
            autoComplete="tel"
            inputMode="tel"
          />
        </Field>

        <Field label={t("q.contact.code_postal")} error={errors?.code_postal}>
          <input
            type="text"
            value={value.code_postal}
            onChange={(e) => update("code_postal", e.target.value.replace(/\D/g, "").slice(0, 5))}
            className={"input " + (errors?.code_postal ? "input-error" : "")}
            inputMode="numeric"
            maxLength={5}
            autoComplete="postal-code"
          />
        </Field>
      </div>

      <label className="mt-6 flex items-start gap-3 text-caption text-ink-soft">
        <input
          type="checkbox"
          checked={value.rgpd_consent}
          onChange={(e) => update("rgpd_consent", e.target.checked as true)}
          className="mt-0.5 h-4 w-4 rounded border-border text-signal focus:ring-signal"
        />
        <span>{t("q.contact.rgpd_consent")}</span>
      </label>
      {errors?.rgpd_consent && (
        <p className="mt-2 text-caption text-danger">{errors.rgpd_consent}</p>
      )}
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-caption font-medium text-ink-soft">{label}</span>
      {children}
      {error && <span className="text-caption text-danger">{error}</span>}
    </label>
  );
}
